import { styled } from "@iventis/styles";
import { TextField } from "@mui/material";
import React, { useState, ChangeEvent, useEffect } from "react";
import { preventMouseWheelInput } from "@iventis/utilities/src/input/number-input-helpers";

interface NumberInputProps {
    /** If value is a number behave as expected, if value is a string become non editable and just display the string */
    value: number | string;
    maxValue?: number;
    minValue?: number;
    decimalPlaces?: number;
    disabled?: boolean;
    testId?: string;
    className?: string;
    onValidValueInputted: (value: number) => void;
}

/** A number input component */
export function NumberInputComponent({
    value,
    maxValue = Number.POSITIVE_INFINITY,
    minValue = Number.NEGATIVE_INFINITY,
    decimalPlaces,
    disabled = false,
    onValidValueInputted,
    testId,
    className,
}: NumberInputProps) {
    const [displayValue, setDisplayValue] = useState(value ?? 0);

    useEffect(() => {
        setDisplayValue(value ?? 0);
    }, [value]);

    const handleValueChanged = (event: ChangeEvent<HTMLInputElement>) => {
        let parsedNumber = Number.parseFloat(event.target.value);
        if (decimalPlaces !== undefined) {
            parsedNumber = Number(parsedNumber.toFixed(decimalPlaces));
        }
        setDisplayValue(parsedNumber);
        if (isNumberValid(parsedNumber)) {
            onValidValueInputted(parsedNumber);
        }
    };

    const handleOnUnfocus = () => {
        if (!isNumberValid(displayValue)) {
            setDisplayValue(value);
        }
    };

    const isNumberValid = (value: number | string) => typeof value === "number" && value <= maxValue && value >= minValue;

    if (typeof value === "string") {
        return <StyledInput className={className} type="text" value={value} disabled={disabled} data-testid={testId} />;
    }

    return (
        <StyledInput
            className={className}
            type="number"
            value={displayValue}
            onBlur={handleOnUnfocus}
            onChange={handleValueChanged}
            disabled={disabled}
            data-testid={testId}
            onWheel={preventMouseWheelInput}
        />
    );
}

const StyledInput = styled(TextField)`
    width: 100%;
`;
